// PODIUM (§6.6): the non-scoring post-match brawl; falls respawn after `TUNE.podium.respawnTicks`.
// The 20 s duration is the room's timer, so the round never ends here. If the champion (the top
// match score, standing on pedestal 1) is knocked off it by a credited hit, DETHRONE names the
// culprit once (server).
import { NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, MapId, ModeId, isHazardCause } from '../ids';
import { MAPS } from '../maps/index';
import { isGrounded } from '../sim/edges';
import type { World } from '../types';
import { tickRespawns } from './common';
import type { ModeRules } from './modeTypes';

/** Mode-local tuning: how recent the culprit's hit must be when the champion comes down. */
export const PODIUM_TUNING = { dethroneHitTicks: 120 } as const;

interface Throne { champion: number; onThrone: boolean; done: boolean }
const thrones = new WeakMap<World, Throne>();

/** The champion: the highest match score among active slots (ties: lowest slot), as the spawns rank them. */
export function podiumChampion(w: World): number {
  let best = NO_SLOT;
  for (const b of w.beans) {
    if (!b.active) continue;
    if (best === NO_SLOT || (w.cfg.matchScores[b.slot] ?? 0) > (w.cfg.matchScores[best] ?? 0)) best = b.slot;
  }
  return best;
}

/** Standing on pedestal 1, the highest raised ground of the Podium (its first spawn). */
function onThrone(w: World, slot: number): boolean {
  const b = w.beans[slot];
  const map = MAPS[MapId.PODIUM];
  const throne = map.groundAt(w, map.spawns[0].x, map.spawns[0].y);
  return b.active && !b.out && isGrounded(b) && b.groundZ >= throne - 1e-6;
}

function watchThrone(w: World): void {
  let t = thrones.get(w);
  if (!t) {
    t = { champion: podiumChampion(w), onThrone: false, done: false };
    thrones.set(w, t);
  }
  if (t.done || t.champion === NO_SLOT) return;
  if (onThrone(w, t.champion)) {
    t.onThrone = true;
    return;
  }
  const b = w.beans[t.champion];
  // Still in the air above the floor: wait for the landing (or the fall).
  if (!t.onThrone || (!isGrounded(b) && b.state !== BS.FALLING && b.state !== BS.RESPAWN && b.state !== BS.LEDGE && b.state !== BS.TEETER)) return;
  t.onThrone = false;
  const culprit = b.touchBy;
  if (culprit === NO_SLOT || culprit === t.champion || isHazardCause(b.touchCause)) return;
  if (w.tick - b.touchTick > PODIUM_TUNING.dethroneHitTicks) return;
  t.done = true;
  emit(w, EV.DETHRONE, t.champion, culprit);
}

export const PODIUM_MODE: ModeRules = {
  id: ModeId.PODIUM,
  name: 'Podium',
  minBeans: 1,
  respawns: true,
  ghosts: false,
  init(w) {
    thrones.set(w, { champion: podiumChampion(w), onThrone: false, done: false });
  },
  onTick(w, _clock, predict) {
    tickRespawns(w, predict);
    if (!predict && w.tick >= 0) watchThrone(w);
  },
  onKO(w, victim) {
    w.beans[victim].respawnTicks = TUNE.podium.respawnTicks;
  },
  onHit() {},
  onTouch() {},
  speedMult() {
    return 1;
  },
  itemsBlocked() {
    return false;
  },
  results() {
    return [];
  },
};
